import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  FileText, 
  Download, 
  Car, 
  Building2, 
  Users, 
  Home,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const ReportsPage = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState(null);
  const [logoBase64, setLogoBase64] = useState('');
  const reportRef = useRef(null);
  const [data, setData] = useState({
    cars: [],
    properties: [],
    workers: [],
    rentals: [],
    expiring: []
  });

  const fetchAllData = async () => {
    if (!token) {
      console.log('No token available');
      return;
    }
    console.log('Fetching data with token...');
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [carsRes, propertiesRes, workersRes, rentalsRes, expiringRes] = await Promise.all([
        axios.get(`${API}/cars`, { headers }),
        axios.get(`${API}/real-estate`, { headers }),
        axios.get(`${API}/workers`, { headers }),
        axios.get(`${API}/rentals`, { headers }),
        axios.get(`${API}/dashboard/expiring`, { headers })
      ]);
      console.log('Data fetched:', {
        cars: carsRes.data.length,
        properties: propertiesRes.data.length,
        workers: workersRes.data.length,
        rentals: rentalsRes.data.length,
        expiring: expiringRes.data.length
      });
      setData({
        cars: carsRes.data || [],
        properties: propertiesRes.data || [],
        workers: workersRes.data || [],
        rentals: rentalsRes.data || [],
        expiring: expiringRes.data || []
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    console.log('Token changed:', token ? 'Available' : 'Not available');
    if (token) {
      fetchAllData();
    }
    // Load logo as base64 from local file
    const loadLogo = async () => {
      try {
        const response = await fetch('/assets/logo.png');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          console.log('Logo loaded successfully');
          setLogoBase64(reader.result);
        };
        reader.readAsDataURL(blob);
      } catch (e) {
        console.log('Could not load logo:', e);
      }
    };
    loadLogo();
  }, [token]);

  const generatePDFFromHTML = async (content, filename) => {
    // Create a temporary container
    const container = document.createElement('div');
    container.innerHTML = content;
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '800px';
    container.style.fontFamily = 'Tajawal, Arial, sans-serif';
    container.style.direction = 'rtl';
    document.body.appendChild(container);

    try {
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(filename);
      toast.success(`تم تحميل ${filename}`);
    } finally {
      document.body.removeChild(container);
    }
  };

  const getReportHeader = () => `
    <div style="padding: 20px; background: white;">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; direction: rtl;">
        <div style="text-align: right; flex: 1;">
          <h1 style="color: #1a365d; font-size: 28px; margin: 0; font-weight: bold;">الشؤون الخاصة</h1>
          <p style="color: #666; font-size: 18px; margin: 5px 0;">د. طارق عبدالجبار</p>
          <p style="color: #999; font-size: 14px; margin: 5px 0;">تاريخ التقرير: ${new Date().toLocaleDateString('ar-SA')}</p>
        </div>
        ${logoBase64 ? `<img src="${logoBase64}" style="width: 150px; height: 150px; object-fit: contain;" />` : ''}
      </div>
      <hr style="border: none; border-top: 2px solid #D4AF37; margin: 15px 0;" />
    </div>
  `;

  // Helper function to get date status and color
  const getDateStatus = (dateStr) => {
    if (!dateStr || dateStr === '-') return { color: '', bg: '' };
    const today = new Date();
    const date = new Date(dateStr);
    const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      // Expired - Red
      return { color: '#DC2626', bg: '#FEE2E2' };
    } else if (diffDays <= 30) {
      // Expiring soon - Yellow/Amber
      return { color: '#D97706', bg: '#FEF3C7' };
    }
    return { color: '', bg: '' };
  };

  const formatDateWithStatus = (dateStr) => {
    if (!dateStr || dateStr === '-') return '-';
    const status = getDateStatus(dateStr);
    if (status.bg) {
      return `<span style="background: ${status.bg}; color: ${status.color}; padding: 4px; border-radius: 4px; font-weight: bold;">${dateStr}</span>`;
    }
    return dateStr;
  };

  const generateCarsReport = async () => {
    setLoading(true);
    setReportType('cars');
    try {
      const rows = data.cars.map(car => `
        <tr>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${car.car_type} ${car.model}</td>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${car.color || '-'}</td>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${car.plate_number_ar || car.plate_number || '-'}</td>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${formatDateWithStatus(car.registration_expiry)}</td>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${formatDateWithStatus(car.insurance_expiry)}</td>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${formatDateWithStatus(car.inspection_expiry)}</td>
        </tr>
      `).join('');

      const content = `
        ${getReportHeader()}
        <div style="padding: 0 20px 20px; direction: rtl;">
          <h2 style="color: #3B82F6; font-size: 18px; margin-bottom: 8px;">تقرير السيارات</h2>
          <p style="color: #666; margin-bottom: 10px; font-size: 13px;">إجمالي السيارات: ${data.cars.length}</p>
          <div style="margin-bottom: 10px; font-size: 10px;">
            <span style="background: #FEE2E2; color: #DC2626; padding: 4px; border-radius: 4px; margin-left: 10px;">منتهي</span>
            <span style="background: #FEF3C7; color: #D97706; padding: 4px; border-radius: 4px;">قريب من الانتهاء</span>
          </div>
          <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
            <thead>
              <tr style="background: #3B82F6; color: white;">
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">السيارة</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">اللون</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">اللوحة</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">الاستمارة</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">التأمين</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">الفحص</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      `;
      await generatePDFFromHTML(content, 'تقرير_السيارات.pdf');
    } catch (error) {
      toast.error('خطأ في إنشاء التقرير');
      console.error(error);
    } finally {
      setLoading(false);
      setReportType(null);
    }
  };

  const generatePropertiesReport = async () => {
    setLoading(true);
    setReportType('properties');
    try {
      const rows = data.properties.map(prop => `
        <tr>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${prop.title}</td>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${prop.deed_number || '-'}</td>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${prop.deed_date || '-'}</td>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${prop.real_estate_registration === 'registered' ? 'تم تسجيله' : 'لم يسجل'}</td>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${prop.is_mortgaged ? 'نعم' : 'لا'}</td>
        </tr>
      `).join('');

      const content = `
        ${getReportHeader()}
        <div style="padding: 0 20px 20px; direction: rtl;">
          <h2 style="color: #10B981; font-size: 18px; margin-bottom: 8px;">تقرير الصكوك العقارية</h2>
          <p style="color: #666; margin-bottom: 10px; font-size: 13px;">إجمالي الصكوك العقارية: ${data.properties.length}</p>
          <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
            <thead>
              <tr style="background: #10B981; color: white;">
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">العقار</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">رقم الصك</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">تاريخ الصك</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">التسجيل العيني</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">رهن</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      `;
      await generatePDFFromHTML(content, 'تقرير_الصكوك العقارية.pdf');
    } catch (error) {
      toast.error('خطأ في إنشاء التقرير');
      console.error(error);
    } finally {
      setLoading(false);
      setReportType(null);
    }
  };

  const generateWorkersReport = async () => {
    setLoading(true);
    setReportType('workers');
    try {
      const rows = data.workers.map(worker => `
        <tr>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${worker.name}</td>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${worker.job_title || worker.nationality || '-'}</td>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${worker.salary?.toLocaleString() || '-'}</td>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${formatDateWithStatus(worker.residency_expiry)}</td>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${formatDateWithStatus(worker.contract_end)}</td>
        </tr>
      `).join('');

      const content = `
        ${getReportHeader()}
        <div style="padding: 0 20px 20px; direction: rtl;">
          <h2 style="color: #8B5CF6; font-size: 18px; margin-bottom: 8px;">تقرير العمالة المنزلية</h2>
          <p style="color: #666; margin-bottom: 10px; font-size: 13px;">إجمالي العمالة: ${data.workers.length}</p>
          <div style="margin-bottom: 10px; font-size: 10px;">
            <span style="background: #FEE2E2; color: #DC2626; padding: 4px; border-radius: 4px; margin-left: 10px;">منتهي</span>
            <span style="background: #FEF3C7; color: #D97706; padding: 4px; border-radius: 4px;">قريب من الانتهاء</span>
          </div>
          <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
            <thead>
              <tr style="background: #8B5CF6; color: white;">
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">الاسم</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">المهنة</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">الراتب</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">انتهاء الإقامة</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">انتهاء العقد</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      `;
      await generatePDFFromHTML(content, 'تقرير_العمالة.pdf');
    } catch (error) {
      toast.error('خطأ في إنشاء التقرير');
      console.error(error);
    } finally {
      setLoading(false);
      setReportType(null);
    }
  };

  const generateRentalsReport = async () => {
    setLoading(true);
    setReportType('rentals');
    try {
      const rows = data.rentals.map(rental => `
        <tr>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${rental.property_name}</td>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${rental.tenant_name || '-'}</td>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${rental.rental_amount?.toLocaleString() || '-'}</td>
          <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${formatDateWithStatus(rental.contract_end)}</td>
        </tr>
      `).join('');

      const content = `
        ${getReportHeader()}
        <div style="padding: 0 20px 20px; direction: rtl;">
          <h2 style="color: #EF4444; font-size: 18px; margin-bottom: 8px;">تقرير الإيجارات</h2>
          <p style="color: #666; margin-bottom: 10px; font-size: 13px;">إجمالي الإيجارات: ${data.rentals.length}</p>
          <div style="margin-bottom: 10px; font-size: 10px;">
            <span style="background: #FEE2E2; color: #DC2626; padding: 4px; border-radius: 4px; margin-left: 10px;">منتهي</span>
            <span style="background: #FEF3C7; color: #D97706; padding: 4px; border-radius: 4px;">قريب من الانتهاء</span>
          </div>
          <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
            <thead>
              <tr style="background: #EF4444; color: white;">
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">العقار</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">المستأجر</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">قيمة الإيجار</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">انتهاء العقد</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      `;
      await generatePDFFromHTML(content, 'تقرير_الإيجارات.pdf');
    } catch (error) {
      toast.error('خطأ في إنشاء التقرير');
      console.error(error);
    } finally {
      setLoading(false);
      setReportType(null);
    }
  };

  const generateExpiringReport = async () => {
    setLoading(true);
    setReportType('expiring');
    try {
      const rows = data.expiring.map(item => {
        const days = Math.ceil((new Date(item.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
        const status = days < 0 ? 'منتهي' : days <= 7 ? 'عاجل' : 'قريب';
        const statusColor = days < 0 ? '#EF4444' : days <= 7 ? '#F59E0B' : '#3B82F6';
        return `
          <tr>
            <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${item.item}</td>
            <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${item.field}</td>
            <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${item.expiry_date}</td>
            <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; color: ${statusColor}; font-weight: bold; font-size: 13px; vertical-align: middle; line-height: 1.6;">${status}</td>
          </tr>
        `;
      }).join('');

      const content = `
        ${getReportHeader()}
        <div style="padding: 0 20px 20px; direction: rtl;">
          <h2 style="color: #F59E0B; font-size: 18px; margin-bottom: 8px;">تقرير الوثائق المنتهية والقريبة من الانتهاء</h2>
          <p style="color: #666; margin-bottom: 10px; font-size: 13px;">إجمالي العناصر: ${data.expiring.length}</p>
          <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
            <thead>
              <tr style="background: #F59E0B; color: white;">
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">العنصر</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">نوع الوثيقة</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">تاريخ الانتهاء</th>
                <th style="padding: 6px 8px; text-align: right; font-size: 13px;">الحالة</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      `;
      await generatePDFFromHTML(content, 'تقرير_الوثائق_المنتهية.pdf');
    } catch (error) {
      toast.error('خطأ في إنشاء التقرير');
      console.error(error);
    } finally {
      setLoading(false);
      setReportType(null);
    }
  };

  const generateFullReport = async () => {
    setLoading(true);
    setReportType('full');
    try {
      let sections = '';

      // Cars section
      if (data.cars.length > 0) {
        const carsRows = data.cars.map(car => `
          <tr>
            <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${car.car_type} ${car.model}</td>
            <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${car.plate_number_ar || '-'}</td>
            <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${formatDateWithStatus(car.registration_expiry)}</td>
          </tr>
        `).join('');
        sections += `
          <h3 style="color: #3B82F6; font-size: 14px; margin: 15px 0 8px;">السيارات (${data.cars.length})</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
            <thead><tr style="background: #3B82F6; color: white;">
              <th style="padding: 6px 8px; text-align: right; font-size: 13px;">السيارة</th>
              <th style="padding: 6px 8px; text-align: right; font-size: 13px;">اللوحة</th>
              <th style="padding: 6px 8px; text-align: right; font-size: 13px;">الاستمارة</th>
            </tr></thead>
            <tbody>${carsRows}</tbody>
          </table>
        `;
      }

      // Properties section
      if (data.properties.length > 0) {
        const propsRows = data.properties.map(prop => `
          <tr>
            <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${prop.title}</td>
            <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${prop.deed_number || '-'}</td>
          </tr>
        `).join('');
        sections += `
          <h3 style="color: #10B981; font-size: 14px; margin: 15px 0 8px;">الصكوك العقارية (${data.properties.length})</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
            <thead><tr style="background: #10B981; color: white;">
              <th style="padding: 6px 8px; text-align: right; font-size: 13px;">العقار</th>
              <th style="padding: 6px 8px; text-align: right; font-size: 13px;">رقم الصك</th>
            </tr></thead>
            <tbody>${propsRows}</tbody>
          </table>
        `;
      }

      // Workers section
      if (data.workers.length > 0) {
        const workersRows = data.workers.map(w => `
          <tr>
            <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${w.name}</td>
            <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${w.salary?.toLocaleString() || '-'}</td>
            <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${formatDateWithStatus(w.residency_expiry)}</td>
          </tr>
        `).join('');
        sections += `
          <h3 style="color: #8B5CF6; font-size: 14px; margin: 15px 0 8px;">العمالة (${data.workers.length})</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
            <thead><tr style="background: #8B5CF6; color: white;">
              <th style="padding: 6px 8px; text-align: right; font-size: 13px;">الاسم</th>
              <th style="padding: 6px 8px; text-align: right; font-size: 13px;">الراتب</th>
              <th style="padding: 6px 8px; text-align: right; font-size: 13px;">انتهاء الإقامة</th>
            </tr></thead>
            <tbody>${workersRows}</tbody>
          </table>
        `;
      }

      // Rentals section
      if (data.rentals.length > 0) {
        const rentalsRows = data.rentals.map(r => `
          <tr>
            <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${r.property_name}</td>
            <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${r.rental_amount?.toLocaleString() || '-'}</td>
            <td style="padding: 4px; border-bottom: 1px solid #eee; text-align: right; font-size: 13px; vertical-align: middle; line-height: 1.6;">${formatDateWithStatus(r.contract_end)}</td>
          </tr>
        `).join('');
        sections += `
          <h3 style="color: #EF4444; font-size: 14px; margin: 15px 0 8px;">الإيجارات (${data.rentals.length})</h3>
          <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
            <thead><tr style="background: #EF4444; color: white;">
              <th style="padding: 6px 8px; text-align: right; font-size: 13px;">العقار</th>
              <th style="padding: 6px 8px; text-align: right; font-size: 13px;">قيمة الإيجار</th>
              <th style="padding: 6px 8px; text-align: right; font-size: 13px;">انتهاء العقد</th>
            </tr></thead>
            <tbody>${rentalsRows}</tbody>
          </table>
        `;
      }

      const content = `
        ${getReportHeader()}
        <div style="padding: 0 20px 20px; direction: rtl;">
          <h2 style="color: #1a365d; font-size: 18px; margin-bottom: 8px;">التقرير الشامل للأصول</h2>
          <p style="color: #666; margin-bottom: 5px; font-size: 13px;">السيارات: ${data.cars.length} | الصكوك العقارية: ${data.properties.length} | العمالة: ${data.workers.length} | الإيجارات: ${data.rentals.length}</p>
          <div style="margin-bottom: 10px; font-size: 10px;">
            <span style="background: #FEE2E2; color: #DC2626; padding: 4px; border-radius: 4px; margin-left: 10px;">منتهي</span>
            <span style="background: #FEF3C7; color: #D97706; padding: 4px; border-radius: 4px;">قريب من الانتهاء</span>
          </div>
          ${sections}
        </div>
      `;
      await generatePDFFromHTML(content, 'التقرير_الشامل.pdf');
    } catch (error) {
      toast.error('خطأ في إنشاء التقرير');
      console.error(error);
    } finally {
      setLoading(false);
      setReportType(null);
    }
  };

  const reportCards = [
    { id: 'full', title: 'التقرير الشامل', description: 'جميع الأصول', icon: FileText, color: '#1a365d', bgColor: '#EFF6FF', action: generateFullReport },
    { id: 'cars', title: 'تقرير السيارات', description: `${data.cars.length} سيارة`, icon: Car, color: '#3B82F6', bgColor: '#EFF6FF', action: generateCarsReport },
    { id: 'properties', title: 'تقرير الصكوك العقارية', description: `${data.properties.length} عقار`, icon: Building2, color: '#10B981', bgColor: '#ECFDF5', action: generatePropertiesReport },
    { id: 'workers', title: 'تقرير العمالة', description: `${data.workers.length} عامل`, icon: Users, color: '#8B5CF6', bgColor: '#F5F3FF', action: generateWorkersReport },
    { id: 'rentals', title: 'تقرير الإيجارات', description: `${data.rentals.length} إيجار`, icon: Home, color: '#EF4444', bgColor: '#FEF2F2', action: generateRentalsReport },
    { id: 'expiring', title: 'الوثائق المنتهية', description: `${data.expiring.length} وثيقة`, icon: AlertTriangle, color: '#F59E0B', bgColor: '#FFFBEB', action: generateExpiringReport }
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 flex-row-reverse">
        <div className="text-right">
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">التقارير</h1>
          <p className="text-slate-500 mt-1">تحميل تقارير PDF للأصول</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reportCards.map((report) => (
          <Card key={report.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-start justify-between flex-row-reverse">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: report.bgColor }}>
                  <report.icon className="w-6 h-6" style={{ color: report.color }} />
                </div>
                <div className="text-right flex-1 mr-4">
                  <h3 className="font-semibold text-lg text-slate-900">{report.title}</h3>
                  <p className="text-slate-500 text-sm mt-1">{report.description}</p>
                </div>
              </div>
              <Button
                onClick={report.action}
                disabled={loading}
                className="w-full mt-4 text-white"
                style={{ backgroundColor: report.color }}
              >
                {loading && reportType === report.id ? (
                  <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 ml-2" />
                )}
                تحميل PDF
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ReportsPage;
